import { useEffect } from "react";

type FontFile = {
  src: string;
  format: "woff2" | "woff" | "ttf" | "otf";
};
type UseFontOptions = {
  family: string;
  weight?: number;
  style?: "normal" | "italic";
  localPath?: string;
  cdnUrl?: string;
  files?: FontFile[]; // <-- novo
  preload?: boolean;
  fallback?: string[];
  async?: boolean;
  onLoad?: () => void;
  onError?: (err: unknown) => void;
  fontDisplay?: "auto" | "block" | "swap" | "fallback" | "optional";
  enabled?: boolean; // <-- Adicionado
};

function getFontId({ family, weight, style, localPath, cdnUrl }: UseFontOptions) {
  const src = localPath || cdnUrl || "";
  return `usefont-${btoa(`${family}-${weight}-${style}-${src}`)}`.replace(/[^a-z0-9]/gi, "");
}

export function useFont({
  family,
  weight = 400,
  style = "normal",
  localPath,
  cdnUrl,
  preload = false,
  async = false,
  onLoad,
  onError,
  fontDisplay = "swap", // <-- padrão
  enabled = true, // <-- padrão true
  files,
}: UseFontOptions) {
  useEffect(() => {
    if (!enabled) return; // <-- Só executa se enabled !== false
    if (typeof document === "undefined") return;

    const id = getFontId({ family, weight, style, localPath, cdnUrl });

    // Carregamento assíncrono via FontFace API
    if (async && (localPath || cdnUrl) && "fonts" in document) {
      const src = localPath || cdnUrl!;
      const fontFace = new FontFace(
        family,
        `url(${src}) format('woff2')`,
        { weight: String(weight), style }
      );
      fontFace.load().then((loadedFace) => {
        (document as Document & { fonts: FontFaceSet }).fonts.add(loadedFace);
        onLoad && onLoad();
      }).catch((err) => {
        onError && onError(err);
      });
      return;
    }

    // Carregamento tradicional (link/preload/style)
    if (document.getElementById(id)) return;

    if (preload && localPath) {
      const preloadId = `${id}-preload`;
      if (!document.getElementById(preloadId)) {
        const link = document.createElement("link");
        link.id = preloadId;
        link.rel = "preload";
        link.as = "font";
        link.type = "font/woff2";
        link.href = localPath;
        link.crossOrigin = "anonymous";
        document.head.appendChild(link);
      }
    }

    const srcs = files && files.length
      ? files.map(f => `url('${f.src}') format('${f.format}')`).join(",\n      ")
      : localPath
        ? `url('${localPath}') format('woff2')`
        : cdnUrl
          ? `url('${cdnUrl}') format('woff2')`
          : "";

    const fontFaceCss = `
      @font-face {
        font-family: '${family}';
        src: ${srcs};
        font-weight: ${weight};
        font-style: ${style};
        font-display: ${fontDisplay};
      }
    `;

    const styleTag = document.createElement("style");
    styleTag.id = id;
    styleTag.appendChild(document.createTextNode(fontFaceCss));
    document.head.appendChild(styleTag);

    return () => {
      const el = document.getElementById(id);
      if (el) document.head.removeChild(el);
      const preloadEl = document.getElementById(`${id}-preload`);
      if (preloadEl) document.head.removeChild(preloadEl);
    };
  }, [family, weight, style, localPath, cdnUrl, preload, async, onLoad, onError, fontDisplay, enabled, files]);
}